import { useEffect, useState, useMemo } from 'react';
import type { DraftState, Manager, TeamLiveData } from '../types';
import type { TeamValuation } from '../lib/valuation';
import { TEAMS_BY_ABBR, teamFullName } from '../constants/teams';
import { maxBidPower } from '../lib/draftLogic';
import { evaluateNominationOptions, type AdvisorSettings, DEFAULT_ADVISOR_SETTINGS } from '../lib/aiAdvisor';
import { TeamLogo } from './TeamLogo';
import { SuggestedValueBadge } from './SuggestedValueBadge';
import { AINominationAdvisor } from './AINominationAdvisor';
import { TeamScoutingCard, type PriorSaleHistory } from './TeamScoutingCard';

interface Props {
  state: DraftState;
  localManager: Manager | null;
  pastPrices: Record<string, number>;
  liveData: Record<string, TeamLiveData>;
  rankings: Record<string, number>;
  valuations: Record<string, TeamValuation>;
  pendingTeam: string | null;
  settings?: AdvisorSettings;
  pastSalesByTeam?: Record<string, PriorSaleHistory[]>;
  onCancelPending: () => void;
  onConfirmNominate: (team: string, bid: number) => Promise<void>;
  onSelectPendingTeam?: (team: string, startingBid?: number) => void;
  onOpenWarRoom?: () => void;
}

export function NominationArea({
  state, localManager, pastPrices, liveData, rankings, valuations,
  pendingTeam, settings = DEFAULT_ADVISOR_SETTINGS, pastSalesByTeam,
  onCancelPending, onConfirmNominate, onSelectPendingTeam, onOpenWarRoom,
}: Props) {
  const nominee = state.managers[state.currentNomineeIndex];
  const isMyTurn = nominee?.id === localManager?.id;
  const power = localManager ? maxBidPower(state, localManager.id) : 0;
  const [bid, setBid] = useState(state.minBid);

  // Compute AI nomination advice for Rob / local user
  const targetManagerId = localManager?.id ?? 'rob';
  const nominationAdvice = useMemo(() => {
    return evaluateNominationOptions(
      state,
      targetManagerId,
      liveData,
      valuations,
      pastPrices,
      settings,
    );
  }, [state, targetManagerId, liveData, valuations, pastPrices, settings]);

  useEffect(() => {
    // Default the bid to suggested value (capped) when the pending team changes
    const v = pendingTeam ? valuations[pendingTeam] : null;
    const start = v ? Math.min(power, Math.max(state.minBid, v.suggested)) : state.minBid;
    setBid(start);
  }, [pendingTeam, state.minBid, power, valuations]);

  const handleApplyAINomination = (team: string, recBid: number) => {
    if (onSelectPendingTeam) {
      onSelectPendingTeam(team, recBid);
    }
    setBid(recBid);
  };

  if (!isMyTurn) {
    return (
      <div className="space-y-4">
        <div className="panel p-6 text-center min-h-[140px] flex flex-col justify-center">
          <p className="text-lg text-slate-300">
            Waiting for <span className="text-2xl font-extrabold text-teal-400">
              {nominee?.emoji} {nominee?.name}
            </span> to nominate…
          </p>
          <p className="text-sm text-slate-500 mt-2">Pick a team on the right to view details while you wait.</p>
        </div>

        {/* AI Nomination Preview while waiting */}
        {nominationAdvice && (
          <AINominationAdvisor
            advice={nominationAdvice}
            onSelectNomination={handleApplyAINomination}
            onOpenWarRoom={onOpenWarRoom}
          />
        )}
      </div>
    );
  }

  if (power < state.minBid) {
    return (
      <div className="panel p-6 text-center border-2 border-rose-500">
        <h2 className="text-2xl font-extrabold text-rose-400 mb-2">No Max Bid Power</h2>
        <p className="text-rose-300">You need at least $1 of bidding power to nominate.</p>
      </div>
    );
  }

  if (!pendingTeam) {
    return (
      <div className="space-y-4">
        <div className="panel p-6 text-center border-2 border-teal-500 bg-teal-950/20">
          <h2 className="text-2xl font-extrabold text-teal-400 mb-1">🎯 {localManager?.name}, your turn to nominate!</h2>
          <p className="text-slate-300 text-sm">Use Rob's AI recommendation below or pick any available team.</p>
          <p className="text-xs text-teal-400 mt-2 font-semibold">Max Bid Power: ${power}</p>
        </div>

        {nominationAdvice && (
          <AINominationAdvisor
            advice={nominationAdvice}
            onSelectNomination={handleApplyAINomination}
            onOpenWarRoom={onOpenWarRoom}
          />
        )}
      </div>
    );
  }

  const meta = TEAMS_BY_ABBR[pendingTeam];
  const past = pastPrices[pendingTeam];
  const rank = rankings[pendingTeam];

  return (
    <div className="panel p-6 border-2 border-teal-500">
      <div className="flex items-center gap-4 mb-4">
        <TeamLogo abbr={pendingTeam} size={56} />
        <div className="flex-1">
          <h2 className="text-2xl font-extrabold text-white">Nominate {teamFullName(pendingTeam)}?</h2>
          <div className="flex flex-wrap gap-2 text-xs mt-1 items-center">
            {valuations[pendingTeam] && (
              <SuggestedValueBadge v={valuations[pendingTeam]} />
            )}
            {past != null && <span className="text-amber-400 font-bold">{state.currentDraftYear - 1}: ${past}</span>}
            {rank && <span className="text-slate-400">Power Rank #{rank}</span>}
            {liveData[pendingTeam]?.projectedWins != null && (
              <span className="text-slate-400">Proj {liveData[pendingTeam].projectedWins!.toFixed(1)}W</span>
            )}
            {liveData[pendingTeam]?.record && (
              <span className="text-slate-400">
                {liveData[pendingTeam].record!.wins}-{liveData[pendingTeam].record!.losses}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Prior Years Sales & Up-To-Date Scouting Intel */}
      <TeamScoutingCard
        abbr={pendingTeam}
        pastSales={pastSalesByTeam?.[pendingTeam]}
      />

      <div className="flex items-center justify-center gap-3 mb-4">
        <span className="text-3xl font-bold text-emerald-400">$</span>
        <input
          type="number"
          min={state.minBid}
          max={power}
          step={1}
          value={bid}
          onChange={e => setBid(Math.max(state.minBid, Math.min(power, Math.floor(Number(e.target.value) || state.minBid))))}
          className="w-32 p-3 text-center text-3xl font-extrabold bg-slate-800 border-2 border-teal-700 rounded-lg text-white"
        />
        <div className="flex flex-col gap-1">
          <button onClick={() => setBid(b => Math.min(power, b + 1))} className="btn-ghost px-3 py-1 text-xs">+1</button>
          <button onClick={() => setBid(b => Math.max(state.minBid, b - 1))} className="btn-ghost px-3 py-1 text-xs">−1</button>
        </div>
      </div>

      <p className="text-center text-xs text-slate-400 mb-4">
        Max Bid Power: <span className="text-teal-400 font-bold">${power}</span>
      </p>

      <div className="flex gap-3">
        <button onClick={onCancelPending} className="btn-ghost flex-1">Cancel</button>
        <button
          onClick={() => onConfirmNominate(pendingTeam, bid)}
          disabled={bid < state.minBid || bid > power || !Number.isInteger(bid)}
          className="btn-primary flex-1"
        >
          Start Auction @ ${bid}
        </button>
      </div>
    </div>
  );
}
